import { CHANNELS } from "../shared/channels";
import { HomeLayout, ModSize, UserSettings } from "../shared/types";
import { call } from "./bridge";
import { byId } from "./dom";
import { stampIcons } from "./icons";
import { t } from "./i18n";
import { reducedMotion, scrollBehavior } from "./motion_pref";
import { canUse } from "./me";
import { buildWidgets, refreshAllWidgets, refreshWidget } from "./widgets";
import { cachedSettings, rememberSettings } from "./settings_cache";
import { buzz } from "./tweens";

// home is a rack of modules. each one is a <section class="mod" data-mod="id"> in index.html that runs itself,
// the rack only orders, sizes and hides them, per person, saved as homeLayout in their settings.
// a new module only needs its markup, an entry here gives it a nicer name and its own sizes

interface ModInfo {
  label: string;
  sizes: ModSize[];
  size: ModSize;
  // starts off the rack until this person adds it from the shelf
  optIn?: boolean;
  // how many items a list module can show, the first is the default
  limits?: number[];
}

const MODS: Record<string, ModInfo> = {
  ask: { label: "ask chi", sizes: ["m", "l"], size: "m" },
  capture: { label: "inbox", sizes: ["s", "m", "l"], size: "l" },
  budget: { label: "budget", sizes: ["s", "m", "l"], size: "s" },
  meters: { label: "gauges", sizes: ["m", "l"], size: "m" },
  tip: { label: "tip of the day", sizes: ["m", "l"], size: "m" },
  board: { label: "feed", sizes: ["m", "l"], size: "m" },
  tasks: { label: "up next", sizes: ["s", "m", "l"], size: "m", limits: [6, 3, 10] },
  routines: { label: "routines", sizes: ["s", "m", "l"], size: "m" },
  notes: { label: "notes", sizes: ["m", "l"], size: "l", limits: [6, 3, 12] },
  voice: { label: "voice", sizes: ["m", "l"], size: "m", optIn: true },
  pings: { label: "pings", sizes: ["s", "m", "l"], size: "m", optIn: true, limits: [4, 2, 8] },
  goals: { label: "goals", sizes: ["m", "l"], size: "m", optIn: true, limits: [4, 2, 8] },
  japanese: { label: "upskill", sizes: ["m", "l"], size: "m", optIn: true },
  leaders: { label: "leaderboard", sizes: ["m", "l"], size: "m", limits: [3, 5, 10] },
  challenge: { label: "draw today", sizes: ["m", "l"], size: "m" },
  poll: { label: "question of the day", sizes: ["m", "l"], size: "m" },
  suggest: { label: "suggestions", sizes: ["m", "l"], size: "l" }
};

const TINTS = ["", "violet", "amber", "green", "red"];
const TINT_COLORS: Record<string, string> = { violet: "var(--violet)", amber: "var(--amber)", green: "var(--green)", red: "var(--red)" };


const SIZE_NAMES: Record<ModSize, string> = { s: "small", m: "half", l: "wide" };
const HOLD_MS = 280;

let rack: HTMLElement;
// the order the markup ships in, new modules slot in next to their neighbours from here
let defaultOrder: string[] = [];
let layout: HomeLayout = emptyLayout();
let saveTimer = 0;

function isHidden(id: string): boolean {
  if (layout.hidden.includes(id)) return true;
  return Boolean(info(id).optIn && !layout.shown?.includes(id));
}

function setHidden(id: string, hidden: boolean): void {
  layout.hidden = layout.hidden.filter((other) => other !== id);
  layout.shown = (layout.shown || []).filter((other) => other !== id);
  if (hidden) layout.hidden.push(id);
  else layout.shown.push(id);
}

// how many items this list module shows, the person's pick or its default
export function modLimit(id: string): number {
  return layout.limits?.[id] || info(id).limits?.[0] || 6;
}

const tuned: Array<() => void> = [];
export function onRackTuned(hook: () => void): void {
  tuned.push(hook);
}

function emptyLayout(): HomeLayout {
  return { order: [], hidden: [], sizes: {}, shown: [], titles: {}, tints: {}, limits: {} };
}

function info(id: string): ModInfo {
  return MODS[id] || { label: id, sizes: ["s", "m", "l"], size: "m" };
}

function mods(): HTMLElement[] {
  return Array.from(rack.querySelectorAll<HTMLElement>(":scope > .mod"));
}

function modById(id: string): HTMLElement | undefined {
  return mods().find((mod) => mod.dataset.mod === id);
}

// saved order first, anything it doesn't know yet goes back after the module it follows in the markup
function resolveOrder(saved: string[]): string[] {
  const order = saved.filter((id, index) => defaultOrder.includes(id) && saved.indexOf(id) === index);
  defaultOrder.forEach((id, index) => {
    if (order.includes(id)) return;
    const before = defaultOrder.slice(0, index).reverse().find((other) => order.includes(other));
    order.splice(before ? order.indexOf(before) + 1 : 0, 0, id);
  });
  return order;
}

function sizeOf(id: string): ModSize {
  const wanted = layout.sizes[id];
  return wanted && info(id).sizes.includes(wanted) ? wanted : info(id).size;
}

// a module's colour re-tints its own accent, and a rename replaces the heading
function paintLook(mod: HTMLElement, id: string): void {
  const tint = layout.tints?.[id] || "";
  if (tint) mod.dataset.tint = tint;
  else delete mod.dataset.tint;
  const heading = mod.querySelector<HTMLElement>(":scope > .section-head > h2");
  if (!heading) return;
  const node = Array.from(heading.childNodes).reverse().find((child) => child.nodeType === Node.TEXT_NODE && child.textContent?.trim());
  if (!node) return;
  mod.dataset.title ??= node.textContent || "";
  node.textContent = layout.titles?.[id] || mod.dataset.title;
}

function paint(): void {
  rack.dataset.density = layout.density || "cozy";
  if (layout.cols) rack.dataset.cols = String(layout.cols);
  else delete rack.dataset.cols;
  resolveOrder(layout.order).forEach((id) => {
    const mod = modById(id);
    if (!mod) return;
    rack.append(mod);
    mod.dataset.size = sizeOf(id);
    mod.classList.toggle("mod-off", isHidden(id));
    paintLook(mod, id);
    const patch = mod.querySelector<HTMLElement>(":scope > .mod-patch");
    const sizeKey = patch?.querySelector<HTMLElement>("[data-act=size]");
    if (sizeKey) {
      sizeKey.textContent = sizeOf(id).toUpperCase();
      sizeKey.setAttribute("aria-label", t("size: {size}, change", { size: t(SIZE_NAMES[sizeOf(id)]) }));
    }
    if (patch) paintPatchText(patch, id);
  });
  paintShelf();
}

// the patch is built before the language is known, so its words are set again on every paint
function paintPatchText(patch: HTMLElement, id: string): void {
  const set = (selector: string, text: string, attribute = "") => {
    const node = patch.querySelector<HTMLElement>(selector);
    if (!node) return;
    if (attribute) node.setAttribute(attribute, text);
    else node.textContent = text;
  };
  set(".patch-label", t(info(id).label));
  set("[data-act=hide]", t("hide"), "aria-label");
  set("[data-act=tune]", t("tune"), "aria-label");
  set("[data-act=back]", t("move earlier"), "aria-label");
  set("[data-act=next]", t("move later"), "aria-label");
  set(".patch-hint", t("hold to drag"));
}

function paintShelf(): void {
  const shelf = byId("rack-shelf");
  const off = resolveOrder(layout.order).filter((id) => isHidden(id) && modById(id) && !modById(id)!.classList.contains("feature-off"));
  shelf.replaceChildren(...off.map((id) => {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "chip rack-chip";
    chip.innerHTML = `<i data-icon="plus"></i><span></span>`;
    chip.querySelector("span")!.textContent = t(info(id).label);
    chip.addEventListener("click", () => {
      setHidden(id, false);
      commit();
      if (id.startsWith("place-")) refreshWidget(id.slice(6));
      modById(id)?.scrollIntoView({ block: "nearest", behavior: scrollBehavior() });
    });
    return chip;
  }));
  stampIcons(shelf);
  shelf.hidden = !rack.classList.contains("arranging") || off.length === 0;
}

function captureOrder(): void {
  layout.order = mods().map((mod) => mod.dataset.mod || "");
}

function save(): void {
  window.clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => {
    void call<UserSettings>(CHANNELS.settingsSave, { homeLayout: layout }).then(rememberSettings).catch(() => undefined);
  }, 450);
}

function commit(): void {
  flip(() => paint());
  save();
}

// first, last, invert, play: modules glide to their new slot instead of jumping
function flip(change: () => void, skip?: HTMLElement): void {
  const before = new Map(mods().map((mod) => [mod, mod.getBoundingClientRect()]));
  change();
  if (reducedMotion()) return;
  mods().forEach((mod) => {
    const old = before.get(mod);
    if (!old || mod === skip || mod.offsetParent === null) return;
    const now = mod.getBoundingClientRect();
    const dx = old.left - now.left;
    const dy = old.top - now.top;
    if (!dx && !dy) return;
    mod.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: "none" }], { duration: 260, easing: "cubic-bezier(0.2, 0.9, 0.3, 1.1)" });
  });
}

function move(id: string, step: number): void {
  captureOrder();
  const visible = layout.order.filter((other) => !isHidden(other) && !modById(other)?.classList.contains("feature-off"));
  const at = visible.indexOf(id);
  const target = visible[at + step];
  if (!target) return;
  const order = layout.order.filter((other) => other !== id);
  order.splice(order.indexOf(target) + (step > 0 ? 1 : 0), 0, id);
  layout.order = order;
  buzz();
  commit();
}

function cycleSize(id: string): void {
  const sizes = info(id).sizes;
  layout.sizes = { ...layout.sizes, [id]: sizes[(sizes.indexOf(sizeOf(id)) + 1) % sizes.length] };
  buzz();
  commit();
}

function hide(id: string): void {
  setHidden(id, true);
  buzz();
  commit();
}

function buildPatch(mod: HTMLElement): void {
  const id = mod.dataset.mod || "";
  const patch = document.createElement("div");
  patch.className = "mod-patch";
  patch.innerHTML = `
    <div class="patch-top"><span class="patch-label"></span><button type="button" class="key-mini" data-act="hide" aria-label=""><i data-icon="close"></i></button></div>
    <div class="patch-keys">
      <button type="button" class="key-mini" data-act="back" aria-label="move earlier"><i data-icon="back"></i></button>
      <button type="button" class="key-mini size" data-act="size"></button>
      <button type="button" class="key-mini" data-act="tune" aria-label="tune"><i data-icon="settings"></i></button>
      <button type="button" class="key-mini" data-act="next" aria-label="move later"><i data-icon="next"></i></button>
    </div>
    <span class="patch-hint"></span>`;
  paintPatchText(patch, id);
  patch.addEventListener("click", (event) => {
    const act = (event.target as Element).closest<HTMLElement>("[data-act]")?.dataset.act;
    if (act === "hide") hide(id);
    if (act === "size") cycleSize(id);
    if (act === "tune") openTune(id);
    if (act === "back") move(id, -1);
    if (act === "next") move(id, 1);
  });
  stampIcons(patch);
  mod.append(patch);
  wireDrag(mod, patch);
}

// hold a module to lift it, then it follows the finger and the others make room
function wireDrag(mod: HTMLElement, patch: HTMLElement): void {
  let holdTimer = 0;
  let dragging = false;
  let start = { x: 0, y: 0 };
  let grab = { x: 0, y: 0 };
  let pointer = { x: 0, y: 0 };
  let scrollFrame = 0;

  const follow = () => {
    mod.style.transform = "";
    const box = mod.getBoundingClientRect();
    mod.style.transform = `translate(${pointer.x - box.left - grab.x}px, ${pointer.y - box.top - grab.y}px) scale(1.02)`;
  };

  // which module the pointer is over, judged by layout boxes (offset*, never moved by transforms) so the
  // gliding neighbours and the lifted copy can't feed back into the hit test. only the middle of a module
  // counts, so a swap has to be meant and the pair can't flip back and forth on the border
  let lastSwap = 0;
  const reorder = () => {
    const now = performance.now();
    if (now - lastSwap < 160) return;
    // the rack is the offset parent of every module, so its own box is the origin
    const frame = rack.getBoundingClientRect();
    const x = pointer.x - frame.left;
    const y = pointer.y - frame.top;
    const inside = (node: HTMLElement, shrink: number) => {
      const padX = node.offsetWidth * shrink;
      const padY = node.offsetHeight * shrink;
      return x > node.offsetLeft + padX && x < node.offsetLeft + node.offsetWidth - padX && y > node.offsetTop + padY && y < node.offsetTop + node.offsetHeight - padY;
    };
    // still over its own slot: nothing to do
    if (inside(mod, 0)) return;
    const list = mods().filter((other) => other.offsetParent !== null);
    const under = list.find((other) => other !== mod && inside(other, 0.2));
    if (!under) return;
    const after = list.indexOf(under) > list.indexOf(mod);
    lastSwap = now;
    flip(() => under[after ? "after" : "before"](mod), mod);
    follow();
  };

  // near the top or bottom edge the page scrolls along
  const edgeScroll = () => {
    const view = byId("view-today");
    const box = view.getBoundingClientRect();
    const edge = 70;
    const speed = pointer.y < box.top + edge ? -12 : pointer.y > box.bottom - edge - 80 ? 12 : 0;
    if (speed) {
      view.scrollTop += speed;
      follow();
      reorder();
    }
    scrollFrame = requestAnimationFrame(edgeScroll);
  };

  const finish = () => {
    window.clearTimeout(holdTimer);
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", finish);
    window.removeEventListener("pointercancel", finish);
    if (!dragging) return;
    dragging = false;
    cancelAnimationFrame(scrollFrame);
    const box = mod.getBoundingClientRect();
    mod.style.transform = "";
    const rest = mod.getBoundingClientRect();
    mod.classList.remove("lifted");
    rack.classList.remove("dragging");
    mod.animate([{ transform: `translate(${box.left - rest.left}px, ${box.top - rest.top}px) scale(1.02)` }, { transform: "none" }], { duration: 220, easing: "cubic-bezier(0.2, 0.9, 0.3, 1.1)" });
    captureOrder();
    save();
    paintShelf();
  };

  const onMove = (event: PointerEvent) => {
    pointer = { x: event.clientX, y: event.clientY };
    if (!dragging) {
      // moving before the hold lands is a scroll, not a drag
      if (Math.hypot(pointer.x - start.x, pointer.y - start.y) > 8) finish();
      return;
    }
    follow();
    reorder();
  };

  patch.addEventListener("pointerdown", (event) => {
    if (event.button !== 0 || (event.target as Element).closest("button")) return;
    start = pointer = { x: event.clientX, y: event.clientY };
    const box = mod.getBoundingClientRect();
    grab = { x: event.clientX - box.left, y: event.clientY - box.top };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", finish);
    holdTimer = window.setTimeout(() => {
      dragging = true;
      buzz(12);
      mod.classList.add("lifted");
      rack.classList.add("dragging");
      follow();
      scrollFrame = requestAnimationFrame(edgeScroll);
    }, event.pointerType === "mouse" ? 120 : HOLD_MS);
  });
  // ios scrolls under a held finger unless the touch is cancelled while a module is lifted
  patch.addEventListener("touchmove", (event) => {
    if (dragging) event.preventDefault();
  }, { passive: false });
  patch.addEventListener("contextmenu", (event) => event.preventDefault());
}

// every widget is a module of its own, hidden until someone adds it from the shelf
function buildPlaceMods(): void {
  buildWidgets(rack, (id, def) => {
    MODS[id] = { label: def.title, sizes: def.sizes, size: def.size, optIn: true };
  });
}

// the tune sheet: this module's name, colour, list length and size, then the look of the whole rack
let tuning = "";

function segment(box: HTMLElement, options: Array<{ label: string; value: string }>, current: string, pick: (value: string) => void): void {
  box.replaceChildren(...options.map((option) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = `seg-option${option.value === current ? " active" : ""}`;
    button.textContent = option.label;
    button.addEventListener("click", () => {
      pick(option.value);
      commit();
      tuned.forEach((hook) => hook());
      paintTune();
    });
    return button;
  }));
}

function paintTune(): void {
  const id = tuning;
  const mod = modById(id);
  if (!id || !mod) return;
  byId("tune-title").textContent = t("tune {name}", { name: t(layout.titles?.[id] || info(id).label) });
  const hasHeading = Boolean(mod.querySelector(":scope > .section-head > h2"));
  byId("tune-name-row").hidden = !hasHeading;
  byId<HTMLInputElement>("tune-name").value = layout.titles?.[id] || "";
  byId<HTMLInputElement>("tune-name").placeholder = t(mod.dataset.title || info(id).label);
  const tints = byId("tune-tints");
  tints.replaceChildren(...TINTS.map((tint) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = `tune-tint${(layout.tints?.[id] || "") === tint ? " active" : ""}`;
    button.setAttribute("aria-label", tint ? t(tint) : t("plain"));
    button.style.setProperty("--swatch", TINT_COLORS[tint] || "var(--chi)");
    button.classList.toggle("plain", !tint);
    button.addEventListener("click", () => {
      layout.tints = { ...layout.tints, [id]: tint };
      commit();
      paintTune();
    });
    return button;
  }));
  const limits = info(id).limits;
  byId("tune-limit-row").hidden = !limits;
  if (limits) {
    segment(byId("tune-limit"), [...limits].sort((first, second) => first - second).map((count) => ({ label: String(count), value: String(count) })), String(modLimit(id)), (value) => {
      layout.limits = { ...layout.limits, [id]: Number(value) };
    });
  }
  segment(byId("tune-size"), info(id).sizes.map((size) => ({ label: t(SIZE_NAMES[size]), value: size })), sizeOf(id), (value) => {
    layout.sizes = { ...layout.sizes, [id]: value as ModSize };
  });
  segment(byId("tune-density"), [{ label: t("cozy"), value: "cozy" }, { label: t("compact"), value: "compact" }], layout.density || "cozy", (value) => {
    layout.density = value as "cozy" | "compact";
  });
  segment(byId("tune-cols"), [{ label: t("auto"), value: "0" }, { label: "2", value: "2" }, { label: "6", value: "6" }], String(layout.cols || 0), (value) => {
    layout.cols = Number(value);
  });
}

function openTune(id: string): void {
  tuning = id;
  paintTune();
  byId("mod-tune").hidden = false;
}

function setupTune(): void {
  const close = () => {
    byId("mod-tune").hidden = true;
    tuning = "";
  };
  byId("tune-close").addEventListener("click", close);
  byId("mod-tune").addEventListener("click", (event) => {
    if (event.target === byId("mod-tune")) close();
  });
  byId("tune-name").addEventListener("input", (event) => {
    const value = (event.target as HTMLInputElement).value.trim().slice(0, 24);
    const titles = { ...layout.titles };
    if (value) titles[tuning] = value;
    else delete titles[tuning];
    layout.titles = titles;
    commit();
  });
}

function setArranging(on: boolean): void {
  rack.classList.toggle("arranging", on);
  const key = byId("rack-arrange");
  key.setAttribute("aria-pressed", String(on));
  key.classList.toggle("accent", on);
  key.querySelector("span")!.textContent = on ? t("done") : t("arrange home");
  byId("rack-reset").hidden = !on;
  paintShelf();
}

export async function setupHomeRack(): Promise<void> {
  rack = byId("home-rack");
  buildPlaceMods();
  defaultOrder = mods().map((mod) => mod.dataset.mod || "");
  mods().forEach(buildPatch);
  byId("rack-arrange").addEventListener("click", () => setArranging(!rack.classList.contains("arranging")));
  setupTune();
  byId("rack-reset").addEventListener("click", () => {
    layout = emptyLayout();
    commit();
    tuned.forEach((hook) => hook());
  });
  paint();
  try {
    const saved = (await cachedSettings()).homeLayout;
    if (saved) layout = { ...emptyLayout(), ...saved, order: saved.order || [], hidden: saved.hidden || [], sizes: saved.sizes || {} };
    paint();
    tuned.forEach((hook) => hook());
    // widgets switched on in the saved layout only become visible now, so this is when they first have something to show
    refreshAllWidgets();
  } catch {
    // the default rack is fine until settings load
  }
}

export function leaveArranging(): void {
  if (rack?.classList.contains("arranging")) setArranging(false);
}
